import {
  type CampaignDonor,
  type Community,
  type CommunityContentItem,
  CommunityContentItemType,
  CommunityType,
  getFullName,
} from "@packages/sdk";
import { getAbsoluteDatesLabel } from "@packages/ui/src/utils/content";
import { utcToZonedTime } from "@packages/ui/src/utils/dateFormat";
import type { SortingState } from "@tanstack/react-table";

import type { LibraryContentItem } from "../../types/parishAdminDashboard";

export const getBasePADUrl = ({ communityId }: { communityId: number }) =>
  `/admin/${communityId}`;

/**
 * Whether to show the Partnership settings tab. Small groups are excluded:
 * they have no `current_contract` of their own, so the page would be empty.
 */
export const getIsPartnerCommunity = (
  community?: Pick<Community, "is_partner" | "type">,
): boolean =>
  !!community?.is_partner && community.type !== CommunityType.small_group;

export const getLibraryContentItemProps = (
  item: CommunityContentItem,
): LibraryContentItem => {
  if (item.collection) {
    const startsAt = utcToZonedTime(item.collection.available_at);
    const endsAt = utcToZonedTime(item.collection.ends_at);
    const availableAt = utcToZonedTime(item.collection.available_at);

    return {
      id: item.id,
      title: item.collection.title,
      description: item.collection.short_desc,
      images: item.collection.images,
      sessions: item.collection.sessions,
      metadata:
        availableAt && endsAt
          ? getAbsoluteDatesLabel(
              availableAt.toISOString(),
              endsAt.toISOString(),
            )
          : item.collection.sessions,
      itemId: item.collection.id,
      referenceType: "collection",
      itemType: item.type,
      startsAt: startsAt,
      endsAt: endsAt,
      url: `/collections/${item.collection.id}`,
      collectionId: item.collection.id,
      communityHasAccess: item.collection.community_has_access,
      joinedCount: item.collection.users_count,
    };
  } else if (item.community_daily) {
    const publishedAt = utcToZonedTime(item.community_daily.published_at);

    return {
      id: item.id,
      title: item.community_daily.collection.title,
      description: item.community_daily.collection.short_desc,
      images: item.community_daily.collection.images,
      sessions: item.community_daily.collection.sessions,
      metadata: `${item.community_daily.collection.sessions || ""}`,
      itemId: item.community_daily.id,
      referenceType: "community_daily",
      itemType: item.type,
      url: `/collections/${item.community_daily.collection.id}`,
      collectionId: item.community_daily.collection.id,
      publishedAt: publishedAt,
      communityHasAccess: item.community_daily.community_has_access,
      isRedeemed: item.community_daily.is_redeemed,
      joinedCount: item.community_daily.joined_count,
    };
  } else if (item.community_challenge) {
    const sessions = item.community_challenge.sessions;
    const startsAt = utcToZonedTime(
      item?.campaign?.starts_at ?? item.community_challenge.starts_at,
    );
    const endsAt = utcToZonedTime(
      item?.campaign?.ends_at ?? item.community_challenge.ends_at,
    );
    const publishedAt = utcToZonedTime(
      item.campaign?.giving?.published_at ??
        item.community_challenge.published_at,
    );

    return {
      id: item.id,
      title: item.campaign?.name ?? item.community_challenge.content_title,
      description:
        item.campaign?.content_title ?? item.community_challenge.short_desc,
      images:
        item.campaign?.content_image ?? item.community_challenge.content_image,
      campaignImages: item.campaign?.image,
      sessions,
      metadata: `${item.campaign?.starts_at && item.campaign?.ends_at ? getAbsoluteDatesLabel(item.campaign.starts_at, item.campaign.ends_at) : item.community_challenge.starts_at && item.community_challenge.ends_at ? getAbsoluteDatesLabel(item.community_challenge.starts_at, item.community_challenge.ends_at) : sessions}`,
      url: item.campaign?.id
        ? `/campaigns/${item.campaign?.id}`
        : item.community_challenge.base_collection_id
          ? `/collections/${item.community_challenge.base_collection_id}`
          : undefined,
      itemId: item.community_challenge.id,
      referenceType: "community_challenge",
      itemType: item.type,
      startsAt: startsAt,
      endsAt: endsAt,
      collectionId: item.community_challenge.base_collection_id,
      publishedAt: publishedAt,
      // Giving campaigns are unlocked for all members, so community_has_access is the wrong signal here.
      communityHasAccess:
        item.campaign?.giving != null ||
        item.community_challenge.community_has_access,
      isRedeemed: item.community_challenge.is_redeemed,
      joinedCount:
        item.campaign?.joined_count ?? item.community_challenge.joined_count,
    };
  }
};

export const formatCurrency = (amountInCents: number): string => {
  return Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    useGrouping: true,
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(amountInCents / 100);
};

export const formatDate = (date: string | Date): string => {
  return new Date(date).toLocaleDateString();
};

export const formatDonorName = (donor: CampaignDonor): string => {
  return getFullName({
    firstName: donor.name,
    lastName: donor.last_name,
  });
};

export const sortDonors = (
  donors: CampaignDonor[],
  sorting: SortingState,
): CampaignDonor[] => {
  if (sorting.length === 0) return donors;

  const sortConfig = sorting[0];
  return [...donors].sort((a, b) => {
    let aValue: number | string;
    let bValue: number | string;

    if (sortConfig.id === "name") {
      aValue = formatDonorName(a).toLowerCase();
      bValue = formatDonorName(b).toLowerCase();
    } else if (sortConfig.id === "amount_in_cents") {
      aValue = a.amount_in_cents;
      bValue = b.amount_in_cents;
    } else if (sortConfig.id === "created_at") {
      aValue = new Date(a.created_at).getTime();
      bValue = new Date(b.created_at).getTime();
    } else {
      return 0;
    }

    if (aValue < bValue) return sortConfig.desc ? 1 : -1;
    if (aValue > bValue) return sortConfig.desc ? -1 : 1;
    return 0;
  });
};

export const formatCommunityContentItemType = (
  itemType: CommunityContentItemType,
): string => {
  if (itemType === CommunityContentItemType.CommunityChallengeAndSession) {
    return "community_challenge";
  } else if (itemType === CommunityContentItemType.CollectionAndSession) {
    return "hallow_global_challenge";
  } else if (itemType === CommunityContentItemType.CommunityDailyAndSession) {
    return "community_daily";
  } else if (itemType === CommunityContentItemType.Campaign) {
    return "giving_campaign";
  }
};
